import React, { useCallback, useEffect, useState } from "react";
import KnowledgeSourceDetailPanel from "./KnowledgeSourceDetailPanel";
import { api } from "../services/api";
import { enrichmentAnchorTypeLabel } from "../config/enrichmentAnchorTypes";
import { formatSourceKind, formatWhen } from "../utils/knowledgeSurfaceKinds";
import { navigateToAddKnowledge } from "../utils/knowledgePrefill";
import { V2_ROUTES } from "../v2/state/useV2Navigation";

/**
 * Entity-scoped reference sources (SA-indexed documents), not map entities.
 */
const EntityReferenceSourcesSection = ({
  entityId,
  entityName = "",
  entityType = "",
  onNavigate = null,
}) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [sourceSearch, setSourceSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState(null);

  const loadSources = useCallback(async () => {
    const eid = String(entityId || "").trim();
    if (!eid) {
      setSources([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await api.listEntityKnowledgeSources(eid);
      setSources(Array.isArray(data?.sources) ? data.sources : []);
    } catch (err) {
      setSources([]);
      setError(err?.message || "Could not load reference sources.");
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  useEffect(() => {
    const q = sourceSearch.trim();
    const eid = String(entityId || "").trim();
    if (!eid || q.length < 2) {
      setSearchResults(null);
      return undefined;
    }
    let cancelled = false;
    setSearchLoading(true);
    (async () => {
      try {
        const rows = await api.searchKnowledgeSources(q, { entityId: eid });
        if (!cancelled) setSearchResults(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceSearch, entityId]);

  const displaySources =
    sourceSearch.trim().length >= 2 && Array.isArray(searchResults)
      ? searchResults
      : sources;
  const visible = expanded ? displaySources : displaySources.slice(0, 3);
  const typeLabel = enrichmentAnchorTypeLabel(entityType).toLowerCase();
  const aboutLabel = entityName || `this ${typeLabel}`;
  const heading = `Sources for this ${typeLabel}`;

  const anchorContext = {
    scopeType: "entity",
    id: entityId,
    name: entityName,
  };

  if (selectedSourceId) {
    return (
      <KnowledgeSourceDetailPanel
        sourceVersionId={selectedSourceId}
        anchorContext={anchorContext}
        onClose={() => setSelectedSourceId(null)}
        onChanged={loadSources}
      />
    );
  }

  if (loading) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-900">{heading}</h3>
        <p className="text-xs text-slate-500 mt-2">Loading indexed documents…</p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{heading}</h3>
          <p className="text-xs text-slate-500 mt-1">
            Documents and notes indexed for search about {aboutLabel} — not
            map entities.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-600 tabular-nums">
            {sources.length} source{sources.length === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            onClick={() =>
              navigateToAddKnowledge(onNavigate, V2_ROUTES.home, {
                scopeType: "entity",
                id: entityId,
                name: entityName,
                extractMode: "auto",
              })
            }
            className="text-xs font-medium text-emerald-800 hover:text-emerald-950"
          >
            Add reference
          </button>
        </div>
      </div>

      {sources.length > 0 ? (
        <input
          type="search"
          value={sourceSearch}
          onChange={(e) => setSourceSearch(e.target.value)}
          placeholder={`Search documents for ${aboutLabel}…`}
          className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs"
        />
      ) : null}
      {searchLoading ? <p className="text-xs text-slate-500 mt-2">Searching…</p> : null}

      {error ? <p className="text-xs text-red-500 mt-3">{error}</p> : null}

      {!error && sources.length === 0 ? (
        <p className="text-xs text-slate-500 mt-3">
          No reference documents indexed yet. Use Add reference or Add knowledge from Home with
          index-only mode (no extraction).
        </p>
      ) : null}

      {visible.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {visible.map((source) => (
            <li key={source.source_version_id}>
              <button
                type="button"
                onClick={() => setSelectedSourceId(source.source_version_id)}
                className="w-full rounded-md border border-slate-100 bg-slate-50/80 px-3 py-2 text-left hover:border-slate-200 hover:bg-slate-50"
              >
                <p className="text-sm font-medium text-slate-900">{source.title}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {formatSourceKind(source.source_kind)}
                  {source.created_at ? ` · ${formatWhen(source.created_at)}` : ""}
                  {source.fragment_count ? ` · ${source.fragment_count} passages` : ""}
                  {source.body_fragment_count != null
                    ? ` · ${source.body_fragment_count} body`
                    : ""}
                </p>
                {source.index_status ? (
                  <p className="text-[11px] mt-0.5">
                    <span
                      className={
                        source.index_status === "indexed"
                          ? "text-emerald-700"
                          : source.index_status === "needs_body" ||
                              source.index_status === "needs_embed"
                            ? "text-amber-700"
                            : "text-slate-500"
                      }
                    >
                      {source.index_status === "indexed"
                        ? "Indexed"
                        : source.index_status === "needs_body"
                          ? "Needs body index"
                          : source.index_status === "needs_embed"
                            ? "Needs embeddings"
                            : source.index_status === "figure_only"
                              ? "Figure captions only"
                              : source.index_status}
                    </span>
                    {source.missing_vector_count
                      ? ` · ${source.missing_vector_count} missing vectors`
                      : ""}
                  </p>
                ) : null}
                {source.text_preview ? (
                  <p className="text-xs text-slate-600 mt-1 line-clamp-2 whitespace-pre-wrap">
                    {source.text_preview}
                  </p>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {displaySources.length > 3 ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-xs font-medium text-blue-700 hover:text-blue-900"
        >
          {expanded ? "Show fewer" : `Show all ${displaySources.length} sources`}
        </button>
      ) : null}
    </section>
  );
};

export default EntityReferenceSourcesSection;
